import { useLang } from 'hooks/lang';

import { Row, IconButton, LabeledSelect } from 'components/Blocks';
import { XIcon } from 'components/Icons';

import { operations } from '../calculations';

import { FormTeamService } from './types';
import { CostCalculationEntry } from 'entities';

interface SingleCalculationProps {
  children?: never;
  service: FormTeamService;
  index: number;
  update(updated: FormTeamService['costCalculation'][number]): void;
  remove(): void;
}

type Operation = FormTeamService['costCalculation'][number]['operation'];

const transformRefIntoString = ({ ref, type }: CostCalculationEntry): string => {
  return `${ref}#${type}`;
};

const extractCostCalculationEntry = (stringified: string): CostCalculationEntry => {
  const [ref, type] = stringified.split('#');

  return {
    ref,
    type: type as CostCalculationEntry['type'],
  };
};

function SingleCalculation({
  index,
  service,
  update,
  remove,
}: SingleCalculationProps): JSX.Element {
  const { t } = useLang();

  const calculation = service.costCalculation[index];
  const { base, operation, value } = calculation;

  const options = [
    ...service.costVariables.map(({ id: ref, name }) => ({
      name,
      id: transformRefIntoString({ ref, type: 'VARIABLE' }),
    })),

    ...service.costCalculation
      .map(({ id: ref }, ind) => ({
        id: transformRefIntoString({ ref, type: 'CALCULATION' }),
        name: `Calculation ${ind + 1}`,
      }))
      .filter((_, ind) => ind < index),
  ];

  return (
    <Row alignItems="flex-end">
      <p className="text-sm font-medium">
        <span className="phone:hidden">{t('calculation')}</span>
        {` ${index + 1}:`}
      </p>

      <LabeledSelect
        containerClassName="w-full max-w-[25%] phone:max-w-full"
        id={`base-${index}`}
        options={options}
        labelKey="name"
        label={t('base') as string}
        value={transformRefIntoString(base)}
        onChangeDirect={(newBase) =>
          update({ ...calculation, base: extractCostCalculationEntry(newBase) })
        }
      />

      <LabeledSelect
        containerClassName="w-full max-w-[25%] phone:max-w-full"
        id={`operation-${index}`}
        label={t('operation') as string}
        value={operation}
        isSearchable={false}
        options={Object.values(operations).map(({ getLabel, ...rest }) => ({
          ...rest,
          label: getLabel(t),
        }))}
        onChangeDirect={(newOperation) =>
          update({
            ...calculation,
            operation: newOperation as Operation,
            value: operations[newOperation as Operation].hasValue
              ? value || { ref: '', type: 'VARIABLE' }
              : undefined,
          })
        }
      />

      {operations[operation].hasValue && (
        <LabeledSelect
          containerClassName="w-full max-w-[25%] phone:max-w-full"
          id={`value-calc-${index}`}
          options={options}
          label={t('value') as string}
          labelKey="name"
          value={transformRefIntoString(value || { ref: '', type: 'VARIABLE' })}
          onChangeDirect={(newValue) =>
            update({ ...calculation, value: extractCostCalculationEntry(newValue) })
          }
        />
      )}

      <IconButton onClick={remove} className="mb-1 self-end">
        <XIcon />
      </IconButton>
    </Row>
  );
}

export default SingleCalculation;
